import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Space, Tag, message, Input, Card, Popconfirm, Tooltip, Spin, Modal, Form, Typography } from 'antd';
import type { TableProps } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { promptService, PromptTemplate, CreatePromptPayload } from '../services/promptService';

const { Search, TextArea } = Input;
const { Text: TypographyText } = Typography;

export const PromptManagement: React.FC = () => {
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm] = Form.useForm();
  const [createContent, setCreateContent] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const navigate = useNavigate();
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [openPrompt, setOpenPrompt] = useState<PromptTemplate | null>(null);
  const [editMode, setEditMode] = useState(false);

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await promptService.getAllPrompts(showInactive);
      setPrompts(data);
    } catch (error: any) {
      message.error(`Failed to load prompts: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]); // Only depend on showInactive, not fetchPrompts to avoid extra calls

  const handleActivate = async (prompt: PromptTemplate) => {
    setActionLoading(prompt.id);
    try {
      await promptService.activatePrompt(prompt.id, prompt.prompt_name);
      message.success(`Version ${prompt.version} activated successfully`);
      fetchPrompts();
    } catch (error: any) {
      message.error(`Failed to activate: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (prompt: PromptTemplate) => {
    setActionLoading(prompt.id);
    try {
      await promptService.deactivatePrompt(prompt.id, prompt.prompt_name);
      message.success(`Version ${prompt.version} deactivated`);
      fetchPrompts();
    } catch (error: any) {
      message.error(`Failed to deactivate: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (prompt: PromptTemplate) => {
    setActionLoading(prompt.id);
    try {
      await promptService.deletePrompt(prompt.id, prompt.prompt_name);
      message.success('Prompt deleted successfully');
      fetchPrompts();
    } catch (error: any) {
      message.error(`Failed to delete: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreatePrompt = async (values: any) => {
    if (!createContent.trim()) {
      message.error('Prompt content cannot be empty');
      return;
    }

    setCreateLoading(true);
    try {
      const existingPrompts = await promptService.getAllPrompts(false);
      const exists = existingPrompts.some(p => p.prompt_name === values.prompt_name);
      
      if (exists) {
        message.error(`Prompt name '${values.prompt_name}' already exists. Please choose a different name.`);
        return;
      }

      const payload: CreatePromptPayload = {
        prompt_name: values.prompt_name,
        content: createContent,
        description: values.description,
        created_by: 'admin',
      };
      
      await promptService.createPrompt(payload);
      message.success('Prompt created successfully');
      setShowCreateModal(false);
      createForm.resetFields();
      setCreateContent('');
      fetchPrompts();
    } catch (error: any) {
      message.error(`Failed to create prompt: ${error.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditPrompt = async (values: any) => {
    if (!createContent.trim()) {
      message.error('Prompt content cannot be empty');
      return;
    }

    setCreateLoading(true);
    try {
      const existingPrompts = await promptService.getAllPrompts(false);
      const exists = existingPrompts.some(p => p.prompt_name === values.prompt_name && p.id !== openPrompt?.id);
      
      if (exists) {
        message.error(`Prompt name '${values.prompt_name}' already exists. Please choose a different name.`);
        return;
      }

      const payload: CreatePromptPayload = {
        prompt_name: values.prompt_name,
        content: createContent,
        description: values.description,
        created_by: 'admin',
      };
      
      await promptService.updatePrompt(openPrompt?.id || '', payload);
      message.success('Prompt updated successfully');
      setShowPromptModal(false);
      createForm.resetFields();
      setCreateContent('');
      fetchPrompts();
    } catch (error: any) {
      message.error(`Failed to update prompt: ${error.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  // Handler to open modal for create
  const handleOpenCreatePrompt = () => {
    setOpenPrompt(null);
    setEditMode(true);
    setShowPromptModal(true);
    createForm.resetFields();
    setCreateContent("");
  }

  // Handler to open modal for view/edit
  const handleOpenPromptDetail = (prompt: PromptTemplate) => {
    setOpenPrompt(prompt);
    setEditMode(false);
    setShowPromptModal(true);
    createForm.setFieldsValue({
      prompt_name: prompt.prompt_name,
      description: prompt.description ?? '',
    });
    setCreateContent(prompt.content);
  }

  const filteredData = prompts.filter((item) =>
    item.prompt_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns: TableProps<PromptTemplate>['columns'] = [
    {
      title: 'Prompt Name',
      dataIndex: 'prompt_name',
      key: 'prompt_name',
      ellipsis: true,
      render: (text: string, record: PromptTemplate) => (
        <div>
          <Button
            type="link"
            style={{ padding: 0, height: 'auto', fontWeight: 600 }}
            onClick={() => handleOpenPromptDetail(record)}
          >
            {text}
          </Button>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
            ID: {record.id.slice(0, 8)}...
          </div>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 240,
      render: (text: string) => (
        <div style={{ maxWidth: 240 }}>
          {text ? (
            <span>{text}</span>
          ) : (
            <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>No description provided</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: PromptTemplate) => {
        if (record.is_deactivated) {
          return <Tag color="red">DISABLED</Tag>;
        }
        return record.is_active ? (
          <Tag color="green">ACTIVE</Tag>
        ) : (
          <Tag color="orange">DRAFT</Tag>
        );
      },
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 80,
      align: 'center' as const,
      render: (version: number) => (
        <div style={{ fontWeight: 600 }}>v{version}</div>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (date: string) => (
        <div style={{ fontSize: 12 }}>
          <div>{new Date(date).toLocaleDateString()}</div>
          <div style={{ color: '#8c8c8c' }}>{new Date(date).toLocaleTimeString()}</div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: PromptTemplate) => (
        <Space size="small" wrap>
          <Tooltip title="View and edit prompt">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenPromptDetail(record)}
            >
              Edit
            </Button>
          </Tooltip>
          
          {!record.is_active && !record.is_deactivated && (
            <Tooltip title="Activate this version">
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleActivate(record)}
                loading={actionLoading === record.id}
              >
                Activate
              </Button>
            </Tooltip>
          )}
          
          {record.is_active && (
            <Tooltip title="Deactivate this version">
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={() => handleDeactivate(record)}
                loading={actionLoading === record.id}
              >
                Deactivate
              </Button>
            </Tooltip>
          )}
          
          {record.is_deactivated && (
            <Tooltip title="Re-enable this version">
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleActivate(record)}
                loading={actionLoading === record.id}
              >
                Enable
              </Button>
            </Tooltip>
          )}
          
          <Popconfirm
            title="Delete this prompt version?"
            description="This action cannot be undone. The prompt will be permanently removed."
            onConfirm={() => handleDelete(record)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete this prompt version">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={actionLoading === record.id}
              >
                Delete
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && prompts.length === 0) {
    return (
      <div className="admin-content-container">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-container">
      <div className="page-header-row">
        <h2 className="page-title">Prompt Management</h2>
        <Space>
          <Button
            icon={<ReloadOutlined />} 
            size="large" 
            onClick={fetchPrompts} 
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />} 
            onClick={handleOpenCreatePrompt}
          >
            Create New Prompt
          </Button>
        </Space>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} prompt${total !== 1 ? 's' : ''}`,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          size="middle"
          rowClassName={(record) => 
            record.is_active ? 'active-prompt-row' : 
            record.is_deactivated ? 'disabled-prompt-row' : 
            'draft-prompt-row'}
        />
      </Card>

      {/* Create Prompt Modal */}
      <Modal
        title={openPrompt ? (editMode ? `Edit Prompt: ${openPrompt.prompt_name}` : `Prompt: ${openPrompt.prompt_name}`) : "Create New Prompt"}
        open={showPromptModal}
        onCancel={() => { setShowPromptModal(false); setEditMode(false); setOpenPrompt(null); }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Form
          form={createForm}
          onFinish={openPrompt ? handleEditPrompt : handleCreatePrompt}
          layout="vertical"
          initialValues={{
            prompt_name: openPrompt?.prompt_name || '',
            description: openPrompt?.description || ''
          }}
        >
          <Form.Item
            name="prompt_name"
            label="Prompt Name"
            rules={[
              { required: true, message: 'Please enter a prompt name' },
              { pattern: /^[a-z0-9_]+$/, message: 'Use lowercase letters, numbers, and underscores only' },
              { min: 3, message: 'Prompt name must be at least 3 characters' },
            ]}
            tooltip="This is the unique identifier used in the backend code"
          >
            <Input
              placeholder="e.g., main_response_prompt"
              maxLength={50}
              size="large"
              disabled={!!openPrompt && !editMode}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter a description' },
              { min: 10, message: 'Description must be at least 10 characters' },
            ]}
          >
            <Input.TextArea
              placeholder="Describe what this prompt does..."
              rows={2}
              maxLength={200}
              showCount
              disabled={!!openPrompt && !editMode}
            />
          </Form.Item>

          <Form.Item
            label="Prompt Content"
            required
            tooltip="Write your prompt content here. Use template variables like ${variable_name}"
          >
            <TypographyText type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              Use template variables like <code style={{ background: '#f5f5f5', padding: '2px 4px', borderRadius: 2 }}>{'${variable_name}'}</code> (e.g., <code style={{ background: '#f5f5f5', padding: '2px 4px', borderRadius: 2 }}>{'${current_year}'}</code>, <code style={{ background: '#f5f5f5', padding: '2px 4px', borderRadius: 2 }}>{'${search_result_str}'}</code>)
            </TypographyText>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 4 }}>
              <Editor
                height="400px"
                defaultLanguage="markdown"
                value={createContent}
                onChange={value => setCreateContent(value || '')}
                options={{
                  minimap: { enabled: true },
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  fontSize: 14,
                  formatOnPaste: true,
                  formatOnType: true,
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  readOnly: !!openPrompt && !editMode
                }}
                theme="light"
              />
            </div>
            <TypographyText type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              Characters: {createContent.length} | Lines: {createContent.split('\n').length}
            </TypographyText>
          </Form.Item>

          {/* Action Buttons */}
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setShowPromptModal(false); setEditMode(false); setOpenPrompt(null); }}>
                Cancel
              </Button>
              {openPrompt && !editMode && (
                <Button type="primary" onClick={() => setEditMode(true)}>
                  Edit
                </Button>
              )}
              {(editMode || !openPrompt) && (
                <Button type="primary" htmlType="submit" loading={createLoading} icon={<SaveOutlined />}>
                  {openPrompt ? 'Save Changes' : 'Create Prompt'}
                </Button>
              )}
            </Space>
          </Form.Item>

          {/* Version / Info Fields for edit or view */}
          {openPrompt && (
            <div style={{ marginTop: 16, fontSize: 13, color: '#888' }}>
              <div>
                <strong>Version:</strong> v{openPrompt.version}
              </div>
              <div>
                <strong>Created:</strong> {new Date(openPrompt.created_at).toLocaleString()}
              </div>
              <div>
                <strong>Last Updated:</strong> {new Date(openPrompt.updated_at).toLocaleString()}
              </div>
              <div>
                <strong>Created By:</strong> {openPrompt.created_by || 'Unknown'}
              </div>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};
